/**
 * LiveCharacterAvatar — a BOUNDED, singular live-animated 3D character avatar
 * for the surfaces where showing the player's real animated character (spinning
 * turntable + showcase backdrop + particles + aura, exactly like the locker
 * hero) matters and the WebGL-context budget is safe: the main-menu identity
 * tile (1 context) and the room seat cards (≤4 seats).
 *
 * This is the LIVE counterpart to {@link Character3DThumbnail} (the cached
 * static-PNG still used in DENSE many-avatar lists — room browser, Hall of Fame
 * — which must NOT get a context per tile). Do NOT use this in those dense
 * lists; each instance holds its own WebGL context (created + disposed on
 * mount/unmount by {@link Character3DPreview}).
 *
 * Layered fallbacks, so a surface is never blank / never black-screens:
 *   1. lazy chunk loading → {@link Character3DHero}'s Suspense shows a 2D
 *      IconAvatar while Three.js downloads;
 *   2. WebGL init/figure failure → the live preview itself swaps to IconAvatar
 *      internally;
 *   3. lazy CHUNK-LOAD failure (offline / bad network) → {@link Lobby3DBoundary}
 *      catches the throw and renders the cached {@link Character3DThumbnail}
 *      still instead of tearing down the surrounding screen.
 */
import type { CharacterIcon } from '@tt/types';
import { Character3DHero } from '../characters/Character3DHero';
import { Character3DThumbnail } from '../characters/Character3DThumbnail';
import { Lobby3DBoundary } from './Lobby3DBoundary';

interface Props {
  icon: CharacterIcon;
  /** Square edge in CSS px. */
  size: number;
  alt?: string;
  /** Allow drag-to-rotate (auto-rotate always runs regardless). Default false —
   *  seats stay drag-free so a stray drag can't fight the seat's action row. */
  interactive?: boolean;
  className?: string;
}

export function LiveCharacterAvatar({ icon, size, alt, interactive = false, className }: Props) {
  return (
    <Lobby3DBoundary
      fallback={
        <Character3DthumbnailFallback icon={icon} size={size} {...(alt !== undefined ? { alt } : {})} />
      }
    >
      <Character3DHero
        icon={icon}
        size={size}
        interactive={interactive}
        {...(className !== undefined ? { className } : {})}
      />
    </Lobby3DBoundary>
  );
}

/** Cached-still fallback used only when the heavy 3D chunk fails to load. Uses
 *  the same `figure` framing (head-to-feet + showcase) the live hero uses so the
 *  degraded state still shows the whole character + flair, just not animated. */
function Character3DthumbnailFallback({ icon, size, alt }: { icon: CharacterIcon; size: number; alt?: string }) {
  return (
    <Character3DThumbnail icon={icon} size={size} frame="figure" circular={false} {...(alt !== undefined ? { alt } : {})} />
  );
}
